import { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase/firebaseConfig';
import { ref, onValue, remove } from 'firebase/database';
import { useAuth } from '../context/AuthContext';

export function useNotifications() {
  const { userProfile, currentUser } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const deleteNotification = useCallback(async (notificationId) => {
    try {
      await remove(ref(db, `notifications/${notificationId}`));
      return true;
    } catch (e) {
      console.error("Failed to delete notification", e);
      return false;
    }
  }, []);

  useEffect(() => {
    if (!currentUser) return;

    const notifRef = ref(db, 'notifications');
    
    // Function to process data
    const processData = (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const now = Date.now();
        const lastSeen = localStorage.getItem('notif_last_seen') || 0;
        
        const filtered = Object.values(data).filter(n => {
          if (n.expiresAt < now) return false;
          
          const role = userProfile?.role || 'user';
          // SUPER ADMIN SEES EVERYTHING
          if (role === 'superadmin') return true;

          const target = n.target;
          if (target === 'all') return true;
          if (target === currentUser.uid) return true; // USER SPECIFIC
          
          if (target === 'user' && !userProfile?.isAdmin) return true;
          if (target === 'moderator' && (role === 'moderator')) return true;
          if (target === 'support' && (role === 'support' || role === 'moderator')) return true;
          return false;
        });

        setNotifications(filtered.sort((a,b) => b.createdAt - a.createdAt));
        setUnreadCount(filtered.filter(n => n.createdAt > lastSeen).length);
      } else {
        setNotifications([]);
        setUnreadCount(0);
      }
    };

    const unsubscribe = onValue(notifRef, processData);

    const handleStorageChange = () => {
      onValue(notifRef, processData, { onlyOnce: true });
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      unsubscribe();
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [currentUser, userProfile]);

  return { notifications, unreadCount, deleteNotification };
}
